// ─── exportToWord ──────────────────────────────────────────────────────
// V92.0 : génération .docx du plan nutrition, à ouvrir dans Word puis
// exporter en PDF natif (qualité premium garantie, accents UTF-8 OK,
// page breaks intelligents, header/footer auto).
//
// Workflow :
//   1. Anissa édite le markdown dans la modal Finaliser
//   2. Click bouton "📄 Word" → cette fonction génère un .docx
//   3. Anissa ouvre dans Word, peaufine si elle veut
//   4. Word → Enregistrer sous → PDF → impression / courrier
//
// Avantages vs jsPDF :
//   - Anissa peut modifier visuellement dans Word (pas besoin du code)
//   - Plus jamais de bugs encoding (UTF-8 natif Word depuis 30 ans)
//   - Page breaks gérés par Word (plus jamais d'orphelins de titre)
//   - Embed fonts gérés par Word
//   - PDF final = 1 clic dans Word (qualité native premium)
//
// On garde les boutons jsPDF actuels pour rétrocompat — ce service est
// un complément, pas un remplacement (du moins pour V92.0).

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  ImageRun,
  HeadingLevel,
  AlignmentType,
  PageNumber,
  Footer,
  Header,
  PageOrientation,
  LevelFormat,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  ShadingType,
  convertInchesToTwip,
} from "docx";
import { saveAs } from "file-saver";
import { mgdReviewV2Client } from "./mgdReviewV2Client";
import { sha256Hex } from "./journeyState";
import { externalPlanAuthorityClient } from './externalPlanAuthority';
import { isExternalPlanInput } from './externalPlanWorkflow';
import { readCurrentPlanDeliveryEvidence } from './planDeliveryContract';
import { supabase } from '../supabaseClient';

export async function sha256WordBlob(blob, cryptoApi = globalThis.crypto) {
  if (!blob || typeof blob.arrayBuffer !== 'function' || !cryptoApi?.subtle?.digest) {
    throw new Error('Empreinte du document Word indisponible — export annulé.');
  }
  let digest;
  try {
    const bytes = await blob.arrayBuffer();
    digest = await cryptoApi.subtle.digest('SHA-256', bytes);
  } catch {
    throw new Error('Empreinte du document Word indisponible — export annulé.');
  }
  const artifactSha256 = Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
  if (!/^[0-9a-f]{64}$/.test(artifactSha256)) {
    throw new Error('Empreinte du document Word invalide — export annulé.');
  }
  return artifactSha256;
}
// V92.5 : reuse extraction logique fiche frigo (sources multiples comme FicheFrigoPreview)
import { extractFridgeDataFromSections, extractMeals, extractSupplements } from "../nutritionPdf";
// Compliance (2026-05-11) : profil légal du praticien (source unique)
import { PRACTITIONER_LEGAL_PROFILE, getPractitionerSignatureLine } from "./practitionerProfile";
// V94.3 : parser canonical pour les entrees supplements (meme que jsPDF)
import { parseSupplementEntriesStructured } from "./nutritionParsers";
// V94.63 : parser unifie aliments interdits (partage avec FicheFrigoPreview + clientAppMapper)
import { buildForbiddenList } from "./foodRestrictionsParser";
// V94.64 : builder canonique partage (coherence E2E avec modal SaaS + app cliente)
import { buildCanonicalFridgeData } from "./fridgeDataBuilder";

// P1.2 — garde de clairance clinique au niveau service (couvre tous les call sites).
import { assertExportCleared } from "./clinicalClearance";
// « Document résultats » — disclaimer nutritionnel (source unique, partagée avec
// le générateur IA pour que le Word et le texte proposé aient la même phrase).
import { RESULTS_DISCLAIMER as RESULTS_DISCLAIMER_DOC } from "./aiResultsExplanation";
import { fetchAvecDelai } from "./delaiDeGarde";

// ─── Palette brand Anissa (cohérente avec PDF actuel) ────────────────
const COLORS = {
  green:    "1A2E1F",
  gold:     "C4A050",
  text:     "333330",
  textSoft: "555550",
  textMute: "8a8a7a",
};

// ─── Helpers de parsing markdown ──────────────────────────────────────

/**
 * V92.4 : parse markdown en sections {title, content} pour reuser
 * extractFridgeDataFromSections du PDF jsPDF (qui attend ce format).
 */
export function parseMarkdownToFlatSections(markdown) {
  if (!markdown?.trim()) return [];
  const lines = markdown.split('\n');
  const sections = [];
  let currentTitle = '';
  let currentLines = [];
  const flush = () => {
    if (currentTitle || currentLines.length > 0) {
      sections.push({ title: currentTitle, content: currentLines.join('\n').trim() });
    }
    currentLines = [];
  };
  for (const rawLine of lines) {
    const line = rawLine.trim();
    const hashMatch = line.match(/^#{2,3}\s+(.+)$/);
    const isAllCapsHeader = !hashMatch && line === line.toUpperCase() && line.length > 5 && line.length < 80;
    if (hashMatch || isAllCapsHeader) {
      flush();
      currentTitle = hashMatch ? hashMatch[1].trim() : line;
    } else {
      currentLines.push(rawLine);
    }
  }
  flush();
  return sections;
}

/**
 * Parse le markdown finalText en blocs structurés (sections + paragraphes + bullets).
 * Format simple : `## TITRE` → section, `- xxx` ou `— xxx` → bullet, sinon → paragraphe.
 */
export function parseMarkdownToBlocks(markdown) {
  if (!markdown?.trim()) return [];
  const lines = markdown.split('\n');
  const blocks = [];
  let currentSection = null;

  const pushSection = () => {
    if (currentSection) blocks.push(currentSection);
    currentSection = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    // Section header : #, ##, ### ou ALL CAPS ligne courte.
    // V97.99 : on capte aussi le `#` unique (H1). Avant, la regex ^#{2,3}
    // ignorait `# Titre` — la ligne tombait alors en paragraphe et le `#`
    // brut fuyait dans le .docx (ex. "# Vos repas, pensés pour vous...").
    const hashMatch = line.match(/^#{1,6}\s+(.+)$/);
    const isAllCapsHeader = !hashMatch && line === line.toUpperCase() && line.length > 5 && line.length < 80;

    if (hashMatch || isAllCapsHeader) {
      pushSection();
      const title = stripInlineMarkdown(hashMatch ? hashMatch[1].trim() : line);
      currentSection = { type: 'section', title, children: [] };
      continue;
    }

    // Bullet : commence par - / — / • / *
    const bulletMatch = line.match(/^([—\-•*·]|\d+[\.\)])\s+(.+)$/);
    if (bulletMatch && currentSection) {
      currentSection.children.push({ type: 'bullet', text: bulletMatch[2].trim() });
      continue;
    }

    // Paragraphe normal
    if (!currentSection) {
      currentSection = { type: 'section', title: 'Introduction', children: [] };
    }
    currentSection.children.push({ type: 'paragraph', text: line });
  }

  pushSection();
  return blocks;
}

// ─── Nettoyage markdown inline (V97.99) ─────────────────────────────
// Le plan est genere en markdown VOLONTAIREMENT (les ##/### servent au
// parsing cote app cliente). Mais a l'EXPORT Word, aucune syntaxe inline
// ne doit fuiter dans le .docx : plus de **, *, _ visibles, plus de # de
// tete de ligne. On ne touche PAS au texte stocke — nettoyage a l'export
// uniquement.

/**
 * Retire toute syntaxe markdown inline d'une chaine (emphase + # de tete).
 * `**gras**` / `*ital*` / `_ital_` → texte nu ; `#`/`##` de debut → retires.
 * Idempotent, robuste aux imbrications simples.
 * @param {string} text
 * @returns {string}
 */
export function stripInlineMarkdown(text) {
  if (!text) return '';
  return String(text)
    // # de tete de ligne (H1..H6) — au cas ou un titre arrive ici
    .replace(/^\s*#{1,6}\s+/, '')
    // gras : **texte** ou __texte__
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    // italique : *texte* ou _texte_ (pas les * isoles / listes)
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/(^|[^\w])_([^_\n]+)_(?=[^\w]|$)/g, '$1$2')
    // code inline `x` → x
    .replace(/`([^`]+)`/g, '$1')
    // filet final : asterisques / underscores d'emphase orphelins residuels
    // (ex. puce mal formee "*Titre**"). On retire les paires restantes puis
    // les marqueurs isoles, sans toucher aux _ internes de mots (snake_case).
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .trim();
}

/**
 * Transforme un texte markdown inline en runs docx (gras/italique reels).
 * `**x**` → run bold, `*x*`/`_x_` → run italic, reste → run normal.
 * Aucun asterisque/underscore d'emphase ne subsiste dans le rendu.
 * @param {string} text
 * @param {object} runOpts - options communes a tous les runs (font, size, color)
 * @returns {import('docx').TextRun[]}
 */
export function buildInlineRuns(text, runOpts = {}) {
  const clean = String(text ?? '').replace(/^\s*#{1,6}\s+/, '');
  // Retire les marqueurs d'emphase orphelins d'un segment de texte normal
  // (les paires equilibrees sont deja converties en runs gras/italique).
  const stripOrphans = (s) => s.replace(/\*/g, '');
  const runs = [];
  // Tokenise sur **gras**, *ital*, _ital_. Les groupes vides sont ignores.
  const regex = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|_[^_\n]+_)/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(clean)) !== null) {
    if (m.index > lastIndex) {
      runs.push(new TextRun({ text: stripOrphans(clean.slice(lastIndex, m.index)), ...runOpts }));
    }
    const token = m[0];
    if (token.startsWith('**') || token.startsWith('__')) {
      runs.push(new TextRun({ text: token.slice(2, -2), ...runOpts, bold: true }));
    } else {
      runs.push(new TextRun({ text: token.slice(1, -1), ...runOpts, italics: true }));
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < clean.length) {
    runs.push(new TextRun({ text: stripOrphans(clean.slice(lastIndex)), ...runOpts }));
  }
  return runs.length ? runs : [new TextRun({ text: stripOrphans(clean), ...runOpts })];
}

// ─── Builders de paragraphes ────────────────────────────────────────

function pCover(text, opts = {}) {
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { before: opts.before || 0, after: opts.after || 200 },
    children: [
      new TextRun({
        text,
        font: "Calibri",
        size: opts.size || 24, // half-points
        bold: opts.bold || false,
        italics: opts.italic || false,
        color: opts.color || COLORS.text,
      }),
    ],
  });
}

function pHeading(text) {
  // V93.0 : signature visuelle Anissa = liseré doré vertical à gauche du titre
  // (reproduit le rectangle doré du PDF jsPDF). Word ne supporte pas les vrais
  // shapes, on simule avec un border left épais + indent.
  return new Paragraph({
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 400, after: 180 },
    indent: { left: 240 },
    children: [
      new TextRun({
        // V97.99 : strip markdown inline avant uppercase (jamais de ** / # visibles)
        text: stripInlineMarkdown(text).toUpperCase(),
        font: "Calibri",
        size: 28,
        bold: true,
        color: COLORS.green,
        characterSpacing: 20,
      }),
    ],
    border: {
      left: { color: COLORS.gold, size: 36, space: 18, style: "single" },
      bottom: { color: "EEEAD9", size: 6, space: 6, style: "single" },
    },
  });
}

function pBody(text) {
  return new Paragraph({
    spacing: { before: 60, after: 60, line: 320 },
    alignment: AlignmentType.JUSTIFIED,
    // V97.99 : runs inline (gras/italique reels, jamais de ** / * / _ visibles)
    children: buildInlineRuns(text, {
      font: "Calibri",
      size: 22,
      color: COLORS.text,
    }),
  });
}

function pBullet(text) {
  return new Paragraph({
    spacing: { before: 40, after: 40, line: 300 },
    indent: { left: convertInchesToTwip(0.3), hanging: convertInchesToTwip(0.2) },
    children: [
      new TextRun({
        text: "• ",
        font: "Calibri",
        size: 22,
        bold: true,
        color: COLORS.gold,
      }),
      // V97.99 : runs inline (gras/italique reels, jamais de ** / * / _ visibles)
      ...buildInlineRuns(text, {
        font: "Calibri",
        size: 22,
        color: COLORS.text,
      }),
    ],
  });
}

function pSpacer(size = 200) {
  return new Paragraph({
    spacing: { before: 0, after: size },
    children: [new TextRun({ text: "" })],
  });
}

function pCoverBigTitle(text) {
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { before: 0, after: 120 },
    children: [
      new TextRun({
        text,
        font: "Calibri",
        size: 80, // 40pt
        bold: true,
        color: COLORS.green,
      }),
    ],
  });
}

function pCoverEyebrow(text) {
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { before: 0, after: 80 },
    children: [
      new TextRun({
        text: text.toUpperCase(),
        font: "Calibri",
        size: 18, // 9pt
        bold: true,
        color: COLORS.gold,
        characterSpacing: 60,
      }),
    ],
  });
}

// V94.3 : detection des supplements via le parser canonical (meme que jsPDF).
// On extrait toutes les entries du markdown source, puis on identifie les blocks
// qui correspondent par leur titre. Garantit la compatibilite parfaite avec le PDF.
//
// Mapping entre cles canonical (k) et labels affiches dans le card :
const SUPP_FIELD_KEY_TO_LABEL = {
  moment: 'Moment',
  dosage: 'Dose',
  sources: 'Sources',
  justification: 'Pourquoi',
  interactions: 'Attention',
  duree: 'Durée',
};

function normalizeName(s) {
  return (s || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
}

function noBordersTable() {
  return {
    top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
  };
}

function pSupplementCard(name, fields) {
  // V94.4 : width OBLIGATOIRE sur cellules columnSpan sinon Word reporte
  // "contenu illisible" (OOXML grid invalide).
  const headerRow = new TableRow({
    children: [new TableCell({
      columnSpan: 2,
      width: { size: 100, type: WidthType.PERCENTAGE },
      shading: { type: ShadingType.SOLID, color: "F5F0E0", fill: "F5F0E0" },
      margins: { top: 160, bottom: 100, left: 240, right: 240 },
      borders: noBordersTable(),
      children: [new Paragraph({
        children: [new TextRun({
          text: name.toUpperCase(),
          font: "Calibri", size: 22, bold: true, color: COLORS.green, characterSpacing: 30,
        })],
      })],
    })],
  });

  const fieldRows = fields.map(f => new TableRow({
    children: [
      new TableCell({
        width: { size: 22, type: WidthType.PERCENTAGE },
        shading: { type: ShadingType.SOLID, color: "F5F0E0", fill: "F5F0E0" },
        margins: { top: 60, bottom: 60, left: 240, right: 80 },
        borders: noBordersTable(),
        children: [new Paragraph({
          children: [new TextRun({
            text: f.label,
            font: "Calibri", size: 18, bold: true, color: COLORS.gold,
          })],
        })],
      }),
      new TableCell({
        width: { size: 78, type: WidthType.PERCENTAGE },
        shading: { type: ShadingType.SOLID, color: "F5F0E0", fill: "F5F0E0" },
        margins: { top: 60, bottom: 60, left: 80, right: 240 },
        borders: noBordersTable(),
        children: [new Paragraph({
          children: [new TextRun({
            text: f.value,
            font: "Calibri", size: 18, color: COLORS.text,
          })],
        })],
      }),
    ],
  }));

  // Padding bas du card (cellule fusionnee — width OBLIGATOIRE V94.4)
  const padBottomRow = new TableRow({
    children: [new TableCell({
      columnSpan: 2,
      width: { size: 100, type: WidthType.PERCENTAGE },
      shading: { type: ShadingType.SOLID, color: "F5F0E0", fill: "F5F0E0" },
      margins: { top: 0, bottom: 100, left: 0, right: 0 },
      borders: noBordersTable(),
      children: [new Paragraph({ children: [new TextRun({ text: "" })] })],
    })],
  });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    // V94.4 : columnWidths explicit pour le grid OOXML (22%/78% sur ~9000 twips)
    columnWidths: [1980, 7020],
    borders: {
      top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      left: { style: BorderStyle.SINGLE, size: 36, color: COLORS.gold },
      right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    },
    rows: [headerRow, ...fieldRows, padBottomRow],
  });
}

// V93.1 : helper pour charger le logo Anissa depuis /public et l'embed dans le .docx
async function loadLogoBytes() {
  try {
    // Le document se génère très bien sans logo, mais pas si l'attente ne
    // finit jamais : l'export Word resterait suspendu sur une image.
    const response = await fetchAvecDelai('/logo-anissa.png', {}, { timeoutMs: 8000 });
    if (!response.ok) return null;
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  } catch (e) {
    console.warn('[exportToWord] logo non chargé:', e?.message || e);
    return null;
  }
}

function pCoverLogo(logoBytes) {
  if (!logoBytes) return null;
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { before: 0, after: 200 },
    children: [
      new ImageRun({
        data: logoBytes,
        transformation: { width: 110, height: 130 },
      }),
    ],
  });
}

// ─── Source merge fiche frigo (V92.5) ─────────────────────────────
// Replique la logique de FicheFrigoPreview.jsx : merge sections > fiche_frigo_json
// > regex extraction. Source primaire = consultation.fiche_frigo_json (edite par
// Anissa dans la modal Fiche frigo). Fallback sur sections markdown puis regex.
function fromFicheJson(json, supplementsText) {
  if (!json || typeof json !== 'object') return null;
  const repas = json.repas || {};
  const supp = json.supplements || {};
  const textSupp = extractSupplements(supplementsText || '');
  const pick = (arr, fallback) => (Array.isArray(arr) && arr.length > 0 ? arr : fallback);
  return {
    breakfast: Array.isArray(repas.petit_dejeuner) ? repas.petit_dejeuner : [],
    lunch: Array.isArray(repas.dejeuner) ? repas.dejeuner : [],
    dinner: Array.isArray(repas.diner) ? repas.diner : [],
    snack: typeof repas.collation === 'string' ? repas.collation : '',
    toFavor: Array.isArray(json.a_privilegier) ? json.a_privilegier : [],
    toLimit: Array.isArray(json.a_limiter) ? json.a_limiter : [],
    hydration: typeof json.hydratation === 'string' ? json.hydratation : '',
    supplements: {
      morningFasting: pick(supp.matin_a_jeun, textSupp.morningFasting),
      breakfast: pick(supp.petit_dejeuner, textSupp.breakfast),
      lunch: pick(supp.midi, textSupp.lunch),
      dinner: pick(supp.soir, textSupp.dinner),
      bedtime: pick(supp.coucher, textSupp.bedtime),
    },
  };
}

// V94.64 : delegue au builder canonique partage. Avant, la logique etait
// dupliquee ici, dans FicheFrigoPreview.jsx et clientAppMapper.js. Risque
// de divergence elimine : 1 seule source de verite.
function buildFridgeData(consultation, sections, client) {
  return buildCanonicalFridgeData(client, consultation, sections);
}

// ─── Builders Fiche Frigo enrichie (V92.4 → V92.5 fix) ─────────────────────

/** Cell d'en-tete de colonne repas (vert sapin sur fond clair) */
function frigoMealHeaderCell(label, widthPct) {
  return new TableCell({
    width: { size: widthPct, type: WidthType.PERCENTAGE },
    shading: { type: ShadingType.SOLID, color: COLORS.green, fill: COLORS.green },
    margins: { top: 100, bottom: 100, left: 120, right: 120 },
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: label.toUpperCase(),
            font: "Calibri",
            size: 18,
            bold: true,
            color: "FAF9F6",
          }),
        ],
      }),
    ],
  });
}

/** Cell de contenu repas (liste d'items, bg blanc cassé) */
function frigoMealBodyCell(items, widthPct) {
  const children = items?.length
    ? items.map((item) =>
        new Paragraph({
          spacing: { before: 30, after: 30 },
          children: [
            new TextRun({ text: "• ", font: "Calibri", size: 18, color: COLORS.gold }),
            new TextRun({ text: item, font: "Calibri", size: 18, color: COLORS.text }),
          ],
        })
      )
    : [
        new Paragraph({
          children: [new TextRun({ text: "—", font: "Calibri", size: 16, color: COLORS.textMute })],
        }),
      ];

  return new TableCell({
    width: { size: widthPct, type: WidthType.PERCENTAGE },
    shading: { type: ShadingType.SOLID, color: "FAF9F6", fill: "FAF9F6" },
    margins: { top: 140, bottom: 140, left: 140, right: 140 },
    children,
  });
}

/** Bloc "À PRIVILÉGIER" / "À LIMITER" — couleur de fond + items en pills */
function frigoListBlock(label, items, accentColor, bgColor) {
  if (!items?.length) return [];
  return [
    new Paragraph({
      spacing: { before: 220, after: 80 },
      children: [
        new TextRun({
          text: label.toUpperCase(),
          font: "Calibri",
          size: 18,
          bold: true,
          color: accentColor,
          characterSpacing: 60,
        }),
      ],
    }),
    new Paragraph({
      shading: { type: ShadingType.SOLID, color: bgColor, fill: bgColor },
      spacing: { before: 0, after: 0, line: 320 },
      children: [
        new TextRun({
          text: "  " + items.join(" · ") + "  ",
          font: "Calibri",
          size: 20,
          color: COLORS.text,
        }),
      ],
    }),
  ];
}

/**
 * Construit la page Fiche Frigo enrichie : header + table 3 colonnes
 * repas + blocs À PRIVILÉGIER / À LIMITER + hydratation.
 * Retourne null si pas assez de data pour faire un rendu utile.
 */
function buildFridgePage(fridgeData, prenom) {
  if (!fridgeData) return null;
  const hasMeals = fridgeData.breakfast?.length || fridgeData.lunch?.length || fridgeData.dinner?.length;
  const hasTags = fridgeData.toFavor?.length || fridgeData.toLimit?.length || fridgeData.forbidden?.length;
  const supp = fridgeData.supplements || {};
  const hasSupp = supp.morningFasting?.length || supp.breakfast?.length || supp.lunch?.length || supp.dinner?.length || supp.bedtime?.length;
  if (!hasMeals && !hasTags && !hasSupp) return null;

  const children = [];

  // Page break + titre
  children.push(
    new Paragraph({
      pageBreakBefore: true,
      spacing: { before: 0, after: 80 },
      children: [
        new TextRun({
          text: "FICHE FRIGO",
          font: "Calibri",
          size: 36,
          bold: true,
          color: COLORS.green,
          characterSpacing: 80,
        }),
      ],
      border: {
        bottom: { color: COLORS.gold, size: 12, space: 6, style: "single" },
      },
    })
  );
  children.push(
    new Paragraph({
      spacing: { before: 60, after: 240 },
      children: [
        new TextRun({
          text: `Repères du quotidien pour ${prenom}.`,
          font: "Calibri",
          size: 18,
          italics: true,
          color: COLORS.textSoft,
        }),
      ],
    })
  );

  // Table 3 colonnes repas
  if (hasMeals) {
    const table = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 6, color: "FAF9F6" },
        insideVertical: { style: BorderStyle.SINGLE, size: 6, color: "FAF9F6" },
      },
      rows: [
        new TableRow({
          tableHeader: true,
          children: [
            // V97.31 — Vocabulaire suisse romand : Dîner = midi, Souper = soir
            frigoMealHeaderCell("Petit-déjeuner", 33),
            frigoMealHeaderCell("Dîner", 34),
            frigoMealHeaderCell("Souper", 33),
          ],
        }),
        new TableRow({
          children: [
            frigoMealBodyCell(fridgeData.breakfast || [], 33),
            frigoMealBodyCell(fridgeData.lunch || [], 34),
            frigoMealBodyCell(fridgeData.dinner || [], 33),
          ],
        }),
      ],
    });
    children.push(table);
  }

  // Collation + hydratation (compact)
  if (fridgeData.snack || fridgeData.hydration) {
    children.push(
      new Paragraph({
        spacing: { before: 240, after: 60 },
        children: [
          new TextRun({
            text: "ESSENTIELS DU JOUR",
            font: "Calibri",
            size: 18,
            bold: true,
            color: COLORS.gold,
            characterSpacing: 80,
          }),
        ],
      })
    );
    if (fridgeData.snack) {
      children.push(
        new Paragraph({
          spacing: { before: 40, after: 40 },
          children: [
            new TextRun({ text: "Collation : ", font: "Calibri", size: 20, bold: true, color: COLORS.green }),
            new TextRun({ text: fridgeData.snack, font: "Calibri", size: 20, color: COLORS.text }),
          ],
        })
      );
    }
    if (fridgeData.hydration) {
      children.push(
        new Paragraph({
          spacing: { before: 40, after: 40 },
          children: [
            new TextRun({ text: "Hydratation : ", font: "Calibri", size: 20, bold: true, color: COLORS.green }),
            new TextRun({ text: fridgeData.hydration, font: "Calibri", size: 20, color: COLORS.text }),
          ],
        })
      );
    }
  }

  // À privilégier (vert) / À limiter (ambre) / Interdit (rouge)
  children.push(...frigoListBlock("À privilégier", fridgeData.toFavor, "2E4E38", "EAEFE9"));
  children.push(...frigoListBlock("À limiter", fridgeData.toLimit, "B85C00", "FBEFD9"));
  children.push(...frigoListBlock("Interdit", fridgeData.forbidden, "A12D2D", "F7DCDC"));

  // V92.5 : MES COMPLEMENTS — table 5 colonnes (matin à jeun / petit-déj / midi / soir / coucher)
  if (hasSupp) {
    children.push(
      new Paragraph({
        spacing: { before: 280, after: 100 },
        children: [
          new TextRun({
            text: "MES COMPLÉMENTS",
            font: "Calibri",
            size: 18,
            bold: true,
            color: COLORS.gold,
            characterSpacing: 80,
          }),
        ],
      })
    );

    // V97.31 — Vocabulaire suisse romand : Dîner = midi, Souper = soir
    const moments = [
      { label: "Matin à jeun", items: supp.morningFasting || [] },
      { label: "Petit-déjeuner", items: supp.breakfast || [] },
      { label: "Dîner", items: supp.lunch || [] },
      { label: "Souper", items: supp.dinner || [] },
      { label: "Coucher", items: supp.bedtime || [] },
    ];

    const headerCells = moments.map((m) => frigoMealHeaderCell(m.label, 20));
    const bodyCells = moments.map((m) => frigoMealBodyCell(m.items, 20));

    const suppTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 6, color: "FAF9F6" },
        insideVertical: { style: BorderStyle.SINGLE, size: 6, color: "FAF9F6" },
      },
      rows: [
        new TableRow({ tableHeader: true, children: headerCells }),
        new TableRow({ children: bodyCells }),
      ],
    });
    children.push(suppTable);
  }

  return children;
}

// ─── V92.7 : Builder magazine layout (paysage, style PDF jsPDF d'origine) ────
// Reproduit le visuel du PDF Fiche Frigo jsPDF : paysage, 3 cards repas en
// haut, MES COMPLÉMENTS pleine largeur en milieu, À PRIVILÉGIER + À LIMITER
// côte à côte, footer foncé avec hydratation/collation/contact.

const MAG_COLORS = {
  darkGreen: "1A2E1F",      // header bands, footer
  cardBody: "FFFFFF",        // body cards repas
  suppHeader: "1A2E1F",      // bandeau MES COMPLÉMENTS
  suppSubBody: "D5E1CF",     // fond pâle vert pour items compléments
  favorBg: "E8F0E5",         // À PRIVILÉGIER pâle
  favorAccent: "1A2E1F",     // titre vert foncé
  limitBg: "F5E5E5",         // À LIMITER pâle
  limitAccent: "8B2D2D",     // titre rouge foncé
  text: "333330",
  textSoft: "555550",
  textWhite: "FAF9F6",
};

function magCardHeaderCell(label, widthPct) {
  return new TableCell({
    width: { size: widthPct, type: WidthType.PERCENTAGE },
    shading: { type: ShadingType.SOLID, color: MAG_COLORS.darkGreen, fill: MAG_COLORS.darkGreen },
    margins: { top: 140, bottom: 140, left: 100, right: 100 },
    borders: noBorders(),
    children: [new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({
        text: label.toUpperCase(),
        font: "Calibri", size: 22, bold: true, color: MAG_COLORS.textWhite, characterSpacing: 30,
      })],
    })],
  });
}

function magCardBodyCell(items, widthPct) {
  const children = [];
  if (items?.length) {
    items.forEach((item, idx) => {
      // Option N label en italique gras
      children.push(new Paragraph({
        spacing: { before: idx === 0 ? 120 : 200, after: 40 },
        children: [new TextRun({
          text: `Option ${idx + 1}`,
          font: "Calibri", size: 18, bold: true, italics: true, color: MAG_COLORS.darkGreen,
        })],
      }));
      // Texte de l'option
      children.push(new Paragraph({
        spacing: { before: 0, after: 40 },
        children: [new TextRun({
          text: item, font: "Calibri", size: 18, color: MAG_COLORS.text,
        })],
      }));
    });
  } else {
    children.push(new Paragraph({
      spacing: { before: 120, after: 120 },
      children: [new TextRun({ text: "—", font: "Calibri", size: 18, color: MAG_COLORS.textSoft })],
    }));
  }
  return new TableCell({
    width: { size: widthPct, type: WidthType.PERCENTAGE },
    shading: { type: ShadingType.SOLID, color: MAG_COLORS.cardBody, fill: MAG_COLORS.cardBody },
    margins: { top: 100, bottom: 220, left: 200, right: 200 },
    borders: noBorders(),
    children,
  });
}

function noBorders() {
  return {
    top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
  };
}

function magSuppSubHeaderCell(label, widthPct) {
  return new TableCell({
    width: { size: widthPct, type: WidthType.PERCENTAGE },
    shading: { type: ShadingType.SOLID, color: MAG_COLORS.suppSubBody, fill: MAG_COLORS.suppSubBody },
    margins: { top: 80, bottom: 60, left: 80, right: 80 },
    borders: noBorders(),
    children: [new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({
        text: label.toUpperCase(),
        font: "Calibri", size: 16, bold: true, color: MAG_COLORS.darkGreen, characterSpacing: 30,
      })],
    })],
  });
}

function magSuppBodyCell(items, widthPct) {
  const children = items?.length
    ? items.map((it, idx) => new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: idx === 0 ? 60 : 30, after: 30 },
        children: [new TextRun({ text: it, font: "Calibri", size: 16, color: MAG_COLORS.text })],
      }))
    : [new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [new TextRun({ text: "—", font: "Calibri", size: 16, color: MAG_COLORS.textSoft })],
      })];
  return new TableCell({
    width: { size: widthPct, type: WidthType.PERCENTAGE },
    shading: { type: ShadingType.SOLID, color: MAG_COLORS.suppSubBody, fill: MAG_COLORS.suppSubBody },
    margins: { top: 40, bottom: 200, left: 80, right: 80 },
    borders: noBorders(),
    children,
  });
}

function magFavLimCell(title, items, bg, accent) {
  const children = [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 160, after: 100 },
      children: [new TextRun({
        text: title.toUpperCase(),
        font: "Calibri", size: 22, bold: true, color: accent, characterSpacing: 40,
      })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: 200, line: 320 },
      children: [new TextRun({
        text: items?.length ? items.join(", ") : "—",
        font: "Calibri", size: 18, color: MAG_COLORS.text,
      })],
    }),
  ];
  return new TableCell({
    width: { size: 50, type: WidthType.PERCENTAGE },
    shading: { type: ShadingType.SOLID, color: bg, fill: bg },
    margins: { top: 100, bottom: 100, left: 200, right: 200 },
    borders: noBorders(),
    children,
  });
}

function buildFridgeMagazinePage(fridgeData, prenom, dateStr) {
  if (!fridgeData) return null;
  const children = [];

  // ─── 1. Titre + date (table 3 cellules pour positionnement) ─────────
  const titleTable = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: noBorders(),
    rows: [new TableRow({
      children: [
        new TableCell({
          width: { size: 25, type: WidthType.PERCENTAGE },
          borders: noBorders(),
          children: [new Paragraph({ children: [new TextRun({ text: "" })] })],
        }),
        new TableCell({
          width: { size: 50, type: WidthType.PERCENTAGE },
          borders: noBorders(),
          children: [new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new TextRun({
              text: `FICHE NUTRITION  —  ${prenom.toUpperCase()}`,
              font: "Calibri", size: 32, bold: true, color: MAG_COLORS.darkGreen, characterSpacing: 40,
            })],
          })],
        }),
        new TableCell({
          width: { size: 25, type: WidthType.PERCENTAGE },
          borders: noBorders(),
          children: [new Paragraph({
            alignment: AlignmentType.RIGHT,
            children: [new TextRun({
              text: dateStr || "",
              font: "Calibri", size: 18, color: MAG_COLORS.textSoft,
            })],
          })],
        }),
      ],
    })],
  });
  children.push(titleTable);

  // ─── 2. Séparateur ───────────────────────────────────────────────
  children.push(new Paragraph({
    spacing: { before: 60, after: 200 },
    border: { bottom: { color: "CCCCBB", size: 6, space: 4, style: "single" } },
    children: [new TextRun({ text: "" })],
  }));

  // ─── 3. Table 3 cards repas ──────────────────────────────────────
  const repasTable = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    columnWidths: [3200, 3200, 3200],
    borders: {
      ...noBorders(),
      insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      insideVertical: { style: BorderStyle.SINGLE, size: 24, color: "FFFFFF" },
    },
    rows: [
      new TableRow({
        tableHeader: true,
        children: [
          magCardHeaderCell("PETIT-DEJEUNER", 33),
          magCardHeaderCell("DEJEUNER", 34),
          magCardHeaderCell("DINER", 33),
        ],
      }),
      new TableRow({
        children: [
          magCardBodyCell(fridgeData.breakfast || [], 33),
          magCardBodyCell(fridgeData.lunch || [], 34),
          magCardBodyCell(fridgeData.dinner || [], 33),
        ],
      }),
    ],
  });
  children.push(repasTable);

  // ─── 4. MES COMPLÉMENTS (header pleine largeur + 5 sous-cols) ─────
  const supp = fridgeData.supplements || {};
  const hasSupp = supp.morningFasting?.length || supp.breakfast?.length || supp.lunch?.length || supp.dinner?.length || supp.bedtime?.length;
  if (hasSupp) {
    children.push(new Paragraph({ spacing: { before: 240 }, children: [new TextRun({ text: "" })] }));
    const suppTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        ...noBorders(),
        insideVertical: { style: BorderStyle.SINGLE, size: 6, color: MAG_COLORS.suppSubBody },
      },
      rows: [
        // Header band pleine largeur
        new TableRow({
          tableHeader: true,
          children: [new TableCell({
            columnSpan: 5,
            width: { size: 100, type: WidthType.PERCENTAGE },
            shading: { type: ShadingType.SOLID, color: MAG_COLORS.suppHeader, fill: MAG_COLORS.suppHeader },
            margins: { top: 140, bottom: 140, left: 100, right: 100 },
            borders: noBorders(),
            children: [new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [new TextRun({
                text: "MES COMPLÉMENTS",
                font: "Calibri", size: 22, bold: true, color: MAG_COLORS.textWhite, characterSpacing: 40,
              })],
            })],
          })],
        }),
        // Sous-headers — V97.31 vocabulaire suisse romand
        new TableRow({
          children: [
            magSuppSubHeaderCell("Matin à jeun", 20),
            magSuppSubHeaderCell("Petit-déjeuner", 20),
            magSuppSubHeaderCell("Dîner", 20),
            magSuppSubHeaderCell("Souper", 20),
            magSuppSubHeaderCell("Coucher", 20),
          ],
        }),
        // Body items
        new TableRow({
          children: [
            magSuppBodyCell(supp.morningFasting || [], 20),
            magSuppBodyCell(supp.breakfast || [], 20),
            magSuppBodyCell(supp.lunch || [], 20),
            magSuppBodyCell(supp.dinner || [], 20),
            magSuppBodyCell(supp.bedtime || [], 20),
          ],
        }),
      ],
    });
    children.push(suppTable);
  }

  // ─── 5. À PRIVILÉGIER + À LIMITER (2 cols) ─────────────────────
  if (fridgeData.toFavor?.length || fridgeData.toLimit?.length) {
    children.push(new Paragraph({ spacing: { before: 240 }, children: [new TextRun({ text: "" })] }));
    const flTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        ...noBorders(),
        insideVertical: { style: BorderStyle.SINGLE, size: 24, color: "FFFFFF" },
      },
      rows: [new TableRow({
        children: [
          magFavLimCell("À PRIVILÉGIER", fridgeData.toFavor || [], MAG_COLORS.favorBg, MAG_COLORS.favorAccent),
          magFavLimCell("À LIMITER", fridgeData.toLimit || [], MAG_COLORS.limitBg, MAG_COLORS.limitAccent),
        ],
      })],
    });
    children.push(flTable);
  }

  // ─── 6. Footer foncé : Hydratation + Collation + Contact ─────────
  children.push(new Paragraph({ spacing: { before: 240 }, children: [new TextRun({ text: "" })] }));
  const footerTable = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: noBorders(),
    rows: [new TableRow({
      children: [
        new TableCell({
          width: { size: 60, type: WidthType.PERCENTAGE },
          shading: { type: ShadingType.SOLID, color: MAG_COLORS.darkGreen, fill: MAG_COLORS.darkGreen },
          margins: { top: 140, bottom: 140, left: 200, right: 100 },
          borders: noBorders(),
          children: [
            ...(fridgeData.hydration ? [new Paragraph({
              spacing: { before: 0, after: 40 },
              children: [
                new TextRun({ text: "HYDRATATION   ", font: "Calibri", size: 16, bold: true, color: MAG_COLORS.textWhite, characterSpacing: 30 }),
                new TextRun({ text: fridgeData.hydration, font: "Calibri", size: 16, color: MAG_COLORS.textWhite }),
              ],
            })] : []),
            ...(fridgeData.snack ? [new Paragraph({
              spacing: { before: 40, after: 0 },
              children: [
                new TextRun({ text: "COLLATION     ", font: "Calibri", size: 16, bold: true, color: MAG_COLORS.textWhite, characterSpacing: 30 }),
                new TextRun({ text: fridgeData.snack, font: "Calibri", size: 16, color: MAG_COLORS.textWhite }),
              ],
            })] : []),
          ],
        }),
        new TableCell({
          width: { size: 40, type: WidthType.PERCENTAGE },
          shading: { type: ShadingType.SOLID, color: MAG_COLORS.darkGreen, fill: MAG_COLORS.darkGreen },
          margins: { top: 140, bottom: 140, left: 100, right: 200 },
          borders: noBorders(),
          children: [new Paragraph({
            alignment: AlignmentType.RIGHT,
            spacing: { before: 60 },
            children: [new TextRun({
              text: "anissa.nutri@gmail.com  ·  www.anissanutrition.ch  ·  076 621 02 05",
              font: "Calibri", size: 16, color: MAG_COLORS.textWhite,
            })],
          })],
        }),
      ],
    })],
  });
  children.push(footerTable);

  return children;
}

// ─── V92.6 : Export standalone Fiche Frigo ────────────────────────
// V92.7 : layout magazine paysage (style PDF jsPDF d'origine).
// Génère un .docx contenant uniquement la Fiche Frigo, à imprimer + plastifier
// + coller sur le frigo de la cliente. Indépendant du plan alimentaire principal.
export async function exportFridgeToWord(client, consultation, fridgeData) {
  const prenom = (client?.prenom || 'Cliente').trim();
  const dateStr = formatFrDate(consultation?.date);

  const pageChildren = buildFridgeMagazinePage(fridgeData, prenom, dateStr);
  if (!pageChildren) {
    throw new Error('Fiche Frigo vide — rien à exporter.');
  }

  const doc = new Document({
    creator: "Anissa Deroubaix Nutrition",
    title: `Fiche Frigo — ${prenom}`,
    styles: {
      default: {
        document: { run: { font: "Calibri", size: 22 } },
      },
    },
    sections: [{
      properties: {
        page: {
          margin: {
            top: convertInchesToTwip(0.5),
            bottom: convertInchesToTwip(0.4),
            left: convertInchesToTwip(0.5),
            right: convertInchesToTwip(0.5),
          },
          size: { orientation: PageOrientation.LANDSCAPE },
        },
      },
      children: pageChildren,
    }],
  });

  const blob = await Packer.toBlob(doc);
  const safeDate = dateStr.replace(/[\/\\:*?"<>|.]/g, '-');
  saveAs(blob, `Fiche-Frigo-${prenom}-${safeDate}.docx`);
}

// ─── « Document résultats » — Word DÉDIÉ et AUTONOME ──────────────
// Export .docx séparé du plan : explique à la cliente ce que montrent ses
// analyses, dans la voix d'Anissa (texte validé par elle, généré via
// aiResultsExplanation.js). NE modifie PAS l'export du plan. Charte de marque
// réutilisée (logo, palette, identité Anissa, disclaimer).
//
// @param {object} client       - { prenom, nom? }
// @param {object} consultation - { date? } (date du document, sinon aujourd'hui)
// @param {string} explanationText - texte VALIDÉ par Anissa (déjà édité)
export async function exportResultsExplanationToWord(client, consultation, explanationText) {
  const text = (explanationText || '').trim();
  if (!text) {
    throw new Error('Document résultats vide — rien à exporter.');
  }

  const prenom = (client?.prenom || 'Cliente').trim();
  const date = formatFrDate(consultation?.date);

  // Charge le logo Anissa (best-effort : le doc reste valide sans logo).
  const logoBytes = await loadLogoBytes();

  const children = [];

  // ─── Papier à en-tête (identité cabinet) ────────────────────────
  // Même grammaire visuelle que la cover du plan : logo, identité en
  // capitales espacées, titre du praticien en italique, filet or court.
  // Les deux livrables doivent sembler sortir du même cabinet.
  const logoPara = pCoverLogo(logoBytes);
  if (logoPara) {
    children.push(pSpacer(120));
    children.push(logoPara);
  } else {
    children.push(pSpacer(420));
  }
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: 40 },
      children: [
        new TextRun({
          text: 'ANISSA DEROUBAIX NUTRITION',
          font: 'Calibri',
          size: 20,
          bold: true,
          color: COLORS.green,
          characterSpacing: 50,
        }),
      ],
    }),
  );
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: 0 },
      children: [
        new TextRun({
          text: PRACTITIONER_LEGAL_PROFILE.title,
          font: 'Calibri',
          size: 16,
          italics: true,
          color: COLORS.textSoft,
        }),
      ],
    }),
  );
  // Filet or court : clôt le papier à en-tête, ouvre le document
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 120, after: 420 },
      indent: { left: convertInchesToTwip(2.9), right: convertInchesToTwip(2.9) },
      border: { bottom: { color: COLORS.gold, size: 6, space: 4, style: 'single' } },
      children: [new TextRun({ text: '' })],
    }),
  );

  // ─── Bloc titre (eyebrow · destinataire · date) ─────────────────
  children.push(pCoverEyebrow('Lecture de tes résultats'));
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 60, after: 100 },
      children: [
        // Georgia : serif élégant installé partout (l'écho Playfair de la
        // charte, avec substitution sûre côté cliente). « Pour » en italique,
        // prénom en romain — l'accent éditorial signature Anissa.
        new TextRun({ text: 'Pour ', font: 'Georgia', size: 52, italics: true, color: COLORS.green }),
        new TextRun({ text: prenom, font: 'Georgia', size: 52, color: COLORS.green }),
      ],
    }),
  );
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: 0 },
      children: [
        new TextRun({
          text: date,
          font: 'Calibri',
          size: 17,
          color: COLORS.textMute,
          characterSpacing: 30,
        }),
      ],
    }),
  );
  // Filet or centré — respiration avant le corps de lettre
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 220, after: 460 },
      indent: { left: convertInchesToTwip(2.6), right: convertInchesToTwip(2.6) },
      border: { bottom: { color: COLORS.gold, size: 6, space: 6, style: 'single' } },
      children: [new TextRun({ text: '' })],
    }),
  );

  // ─── Corps (texte validé par Anissa) ────────────────────────────
  // On isole le disclaimer s'il est présent en fin de texte pour le styler à
  // part ; sinon on l'ajoute (garantie qu'il figure toujours dans le document).
  // R1 (clinique-guard) — détection TOLÉRANTE du disclaimer en fin de texte.
  // Si Anissa le reformule (casse, ponctuation, léger wording), on le retire
  // quand même du corps pour ne pas le dupliquer : le disclaimer canonique est
  // TOUJOURS ré-ajouté en clôture (plus bas). Clé de reconnaissance robuste,
  // insensible aux accents/casse/ponctuation.
  const normalizeForMatch = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  const DISCLAIMER_KEY = 'ne remplace pas un avis medical';

  let paragraphs = text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (
    paragraphs.length &&
    normalizeForMatch(paragraphs[paragraphs.length - 1]).includes(DISCLAIMER_KEY)
  ) {
    paragraphs = paragraphs.slice(0, -1);
  }

  // Parse ligne par ligne : un titre reste reconnu même si ChatGPT ne place
  // pas de ligne vide entre `## TITRE` et le paragraphe suivant.
  const resultBlocks = parseMarkdownToBlocks(paragraphs.join('\n\n'));
  resultBlocks.forEach((section) => {
    if (section.title && section.title !== 'Introduction') {
      children.push(
        new Paragraph({
          spacing: { before: 320, after: 130, line: 300 },
          keepNext: true,
          children: [new TextRun({
            text: stripInlineMarkdown(section.title),
            font: 'Georgia',
            size: 28,
            bold: true,
            color: COLORS.green,
          })],
        }),
      );
    }
    section.children.forEach((entry) => {
      if (entry.type === 'bullet') {
        children.push(
        new Paragraph({
          bullet: { level: 0 },
          spacing: { before: 0, after: 100, line: 320 },
          children: buildInlineRuns(entry.text, {
            font: 'Calibri', size: 23, color: COLORS.text,
          }),
        }));
        return;
      }
      children.push(
        new Paragraph({
          spacing: { before: 0, after: 200, line: 360 },
          alignment: AlignmentType.LEFT,
          children: buildInlineRuns(entry.text, {
            font: 'Calibri', size: 23, color: COLORS.text,
          }),
        }),
      );
    });
  });

  // ─── Clôture : filet or discret + disclaimer nutritionnel ───────
  // Toujours présent, en registre discret (petit, italique, gris chaud),
  // introduit par le même filet or court que le reste du document.
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 480, after: 200 },
      indent: { left: convertInchesToTwip(3.1), right: convertInchesToTwip(3.1) },
      border: { bottom: { color: COLORS.gold, size: 6, space: 4, style: 'single' } },
      children: [new TextRun({ text: '' })],
    }),
  );
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: 120 },
      indent: { left: convertInchesToTwip(1), right: convertInchesToTwip(1) },
      children: [
        new TextRun({
          // Toujours la version canonique : toute variante reformulée par
          // Anissa a déjà été retirée du corps plus haut (détection tolérante).
          text: RESULTS_DISCLAIMER_DOC,
          font: 'Calibri',
          size: 16,
          italics: true,
          color: COLORS.textMute,
        }),
      ],
    }),
  );

  // ─── Pied de page (même grammaire que le plan) ──────────────────
  // Hairline crème + identité / pagination / mention Confidentiel :
  // les deux livrables du cabinet partagent exactement le même pied.
  const cellNoBorders = {
    top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  };
  const footerCell = (width, alignment, runs) =>
    new TableCell({
      width: { size: width, type: WidthType.PERCENTAGE },
      borders: cellNoBorders,
      margins: { top: 100, bottom: 0, left: 0, right: 0 },
      children: [new Paragraph({ alignment, children: runs })],
    });
  const docFooter = new Footer({
    children: [
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 6, color: 'EEEAD9' },
          bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
          left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
          right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
          insideHorizontal: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
          insideVertical: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
        },
        rows: [new TableRow({
          children: [
            footerCell(33, AlignmentType.LEFT, [
              new TextRun({ text: 'Anissa Deroubaix Nutrition', font: 'Calibri', size: 16, color: COLORS.textMute }),
            ]),
            footerCell(34, AlignmentType.CENTER, [
              new TextRun({ children: [PageNumber.CURRENT], font: 'Calibri', size: 16, color: COLORS.textMute }),
              new TextRun({ text: ' / ', font: 'Calibri', size: 16, color: COLORS.textMute }),
              new TextRun({ children: [PageNumber.TOTAL_PAGES], font: 'Calibri', size: 16, color: COLORS.textMute }),
            ]),
            footerCell(33, AlignmentType.RIGHT, [
              new TextRun({ text: 'Confidentiel', font: 'Calibri', size: 16, color: COLORS.textMute }),
            ]),
          ],
        })],
      }),
    ],
  });

  const doc = new Document({
    creator: 'Anissa Deroubaix Nutrition',
    title: `Lecture de tes résultats — ${prenom}`,
    description: `Lecture nutritionnelle des analyses de ${prenom}`,
    styles: {
      default: { document: { run: { font: 'Calibri', size: 22 } } },
    },
    sections: [
      {
        properties: {
          page: {
            // Marges lettre : colonne de texte un peu plus étroite que le
            // plan (1.15" latéral) — registre courrier de cabinet, lecture
            // confortable.
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1.15),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1.15),
            },
            size: { orientation: PageOrientation.PORTRAIT },
          },
        },
        footers: { default: docFooter },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const safeDate = date.replace(/[\/\\:*?"<>|.]/g, '-');
  const filename = `Resultats-${slugify(prenom)}-${safeDate}.docx`;
  saveAs(blob, filename);
  return { ok: true, filename };
}

// ─── Document builder principal ────────────────────────────────────

/**
 * @param {object} client - { prenom, nom?, form?: { ... } }
 * @param {object} consultation - { date, objective?, ... }
 * @param {string} finalText - markdown du plan édité
 */
export async function exportPlanToWord(client, consultation, finalText, options = {}) {
  // P1.2 — Clairance clinique (fail-closed). Le gate vit ICI, pas sur le bouton,
  // pour couvrir tous les call sites (JourneyPlanEditor, ClientJourneyPage,
  // NutritionConsultation). Throw ExportClinicalError sauf override conscient.
  const clientId = String(client?.id || '');
  const consultationId = String(consultation?.id || '');
  const mgdStatus = await mgdReviewV2Client.status(clientId);
  const externalInput = consultation?.editorial_overrides?.external_plan_input
    ?? consultation?.editorialOverrides?.external_plan_input;
  const usesExternalMode = isExternalPlanInput(externalInput);
  const hasActiveMgd = mgdStatus.reports.length > 0 && !usesExternalMode;
  const persistedText = String(
    consultation?.nutrition_plan ?? consultation?.nutritionPlan ?? consultation?.plan_text ?? '',
  ).replace(/\r\n/g, '\n');
  const requestedText = String(finalText || '').replace(/\r\n/g, '\n');
  if (!persistedText || requestedText !== persistedText) {
    throw new Error('Le texte à exporter ne correspond pas au plan enregistré et validé. Recharge la version courante.');
  }
  await readCurrentPlanDeliveryEvidence({ client, consultation, db: supabase });
  let persistedPlanHash = null;
  if (usesExternalMode) {
    if (!consultationId) throw new Error('Version persistée du plan externe introuvable. Export bloqué.');
    persistedPlanHash = await sha256Hex(String(finalText || '').replace(/\r\n/g, '\n'));
    const externalAuthority = await externalPlanAuthorityClient.status({
      clientId, consultationId, planHash: persistedPlanHash,
      profileSummaryHash: externalInput.profile_summary_sha256,
    });
    if (!externalAuthority.authorized || !externalAuthority.persisted
        || !externalAuthority.validated) {
      throw new Error('Le plan externe doit être enregistré et validé par Anissa avant export.');
    }
  } else if (hasActiveMgd) {
    if (!consultationId) throw new Error('Version enregistrée du plan introuvable. Export bloqué.');
    persistedPlanHash = await sha256Hex(String(finalText || '').replace(/\r\n/g, '\n'));
    const proof = await mgdReviewV2Client.planProofStatus({ clientId, consultationId });
    if (!proof.authorized || !proof.persisted || proof.planHash !== persistedPlanHash) {
      throw new Error('Le texte courant ne correspond plus au plan autorisé et enregistré. Export bloqué.');
    }
  } else {
    assertExportCleared(finalText, { form: client?.form }, options);
  }

  const prenom = (client?.prenom || 'Cliente').trim();
  const date = formatFrDate(consultation?.date);
  const objectif = (
    client?.form?.objectif
    || client?.form?.objective
    || client?.form?.objectifPrincipalNutrition
    || ''
  ).trim();

  const blocks = parseMarkdownToBlocks(finalText || '');

  // V92.5 : extraction Fiche Frigo enrichie via buildFridgeData (merge sources :
  // sections markdown > consultation.fiche_frigo_json > regex fallback).
  // Reproduit fidelement la logique de FicheFrigoPreview.jsx pour avoir la
  // MEME donnee structuree que la modal "Fiche Frigo" du SaaS et le PDF jsPDF.
  // Inclut aussi forbidden (form.allergies + alimentsEvites) + supplements 5 moments.
  const flatSections = parseMarkdownToFlatSections(finalText || '');
  let fridgeData = null;
  try {
    fridgeData = buildFridgeData(consultation, flatSections, client);
  } catch {
    fridgeData = null;
  }

  // V93.1 : charger le logo Anissa pour l'embed cover (PNG depuis /public)
  const logoBytes = await loadLogoBytes();

  // ─── COVER PAGE ─────────────────────────────────────────────────
  const coverChildren = [
    // espace haut réduit pour laisser de la place au logo
    pSpacer(1200),
  ];
  // Logo en haut (si disponible)
  const logoPara = pCoverLogo(logoBytes);
  if (logoPara) {
    coverChildren.push(logoPara);
    coverChildren.push(pSpacer(200));
  }
  coverChildren.push(pCoverEyebrow("Plan nutritionnel"));
  coverChildren.push(pCoverBigTitle("Personnalisé"));
  coverChildren.push(pSpacer(200));
  coverChildren.push(pCover(prenom, { size: 48, bold: true, color: COLORS.green }));
  coverChildren.push(pSpacer(120));

  if (objectif) {
    coverChildren.push(pCoverEyebrow("Objectif"));
    coverChildren.push(
      pCover(objectif, {
        size: 22,
        italic: true,
        color: COLORS.textSoft,
      })
    );
  }

  coverChildren.push(pSpacer(800));
  coverChildren.push(pCover(date, { size: 20, color: COLORS.textMute }));
  coverChildren.push(pSpacer(2400));
  coverChildren.push(
    pCover("Anissa Deroubaix Nutrition", {
      size: 22,
      bold: true,
      color: COLORS.green,
    })
  );
  coverChildren.push(
    pCover(PRACTITIONER_LEGAL_PROFILE.title, {
      size: 18,
      italic: true,
      color: COLORS.textSoft,
    })
  );
  coverChildren.push(
    pCover("AB Coaching Sarl · Rue de Rive 28 · 1260 Nyon", {
      size: 16,
      color: COLORS.textMute,
    })
  );
  // Page break après cover
  coverChildren.push(
    new Paragraph({
      children: [new TextRun({ text: "", break: 1 })],
      pageBreakBefore: false,
    })
  );

  // ─── CONTENU PRINCIPAL ────────────────────────────────────────
  // V94.3 : detection sections "supplement" via parser canonical (meme que jsPDF).
  // Strategie : extraire TOUS les supplement entries du markdown via le parser
  // canonical, puis identifier les blocks dont le titre correspond a une entry —
  // ces blocks sont rendus en card, les autres en pHeading classique.
  const supplementEntries = parseSupplementEntriesStructured(finalText || '');
  const supplementByName = new Map();
  for (const entry of supplementEntries) {
    supplementByName.set(normalizeName(entry.name), entry);
  }

  const contentChildren = [];
  for (const block of blocks) {
    if (block.type === 'section') {
      // V92.4 : skip section Fiche Frigo si fridgeData (rendu enrichi separe)
      if (fridgeData && /fiche\s*frigo|frigo/i.test(block.title)) continue;

      // V94.3 : block dont le titre matche une entry supplement → render en card
      const matchedSupp = supplementByName.get(normalizeName(block.title));
      if (matchedSupp) {
        const fields = [];
        for (const [key, value] of Object.entries(matchedSupp.fields || {})) {
          if (!value) continue;
          fields.push({ label: SUPP_FIELD_KEY_TO_LABEL[key] || key, value: String(value) });
        }
        if (fields.length >= 2) {
          contentChildren.push(pSpacer(180));
          contentChildren.push(pSupplementCard(matchedSupp.name, fields));
          contentChildren.push(pSpacer(120));
          continue;
        }
      }

      contentChildren.push(pHeading(block.title));
      for (const child of block.children) {
        if (child.type === 'bullet') {
          contentChildren.push(pBullet(child.text));
        } else if (child.type === 'paragraph') {
          contentChildren.push(pBody(child.text));
        }
      }
      contentChildren.push(pSpacer(120));
    }
  }

  // V92.6 : Fiche Frigo retirée du plan principal — désormais exportée en
  // .docx séparé via exportFridgeToWord (modal Fiche Frigo) pour pouvoir être
  // imprimée + plastifiée + collée sur le frigo, indépendamment du plan.

  // ─── HEADER / FOOTER (pages 2+) ───────────────────────────────
  // V93.0 : header layout 3 colonnes (prénom · titre · date) + ligne séparatrice
  // pour matcher le PDF jsPDF.
  const docHeader = new Header({
    children: [
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          bottom: { style: BorderStyle.SINGLE, size: 6, color: "EEEAD9" },
          left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        },
        rows: [new TableRow({
          children: [
            new TableCell({
              width: { size: 33, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
              },
              margins: { top: 0, bottom: 60, left: 0, right: 0 },
              children: [new Paragraph({
                alignment: AlignmentType.LEFT,
                children: [new TextRun({
                  text: prenom.toLowerCase(),
                  font: "Calibri", size: 16, color: COLORS.textMute,
                })],
              })],
            }),
            new TableCell({
              width: { size: 34, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
              },
              margins: { top: 0, bottom: 60, left: 0, right: 0 },
              children: [new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [new TextRun({
                  text: "Plan nutrition personnalisé",
                  font: "Calibri", size: 16, color: COLORS.textMute,
                })],
              })],
            }),
            new TableCell({
              width: { size: 33, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
              },
              margins: { top: 0, bottom: 60, left: 0, right: 0 },
              children: [new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [new TextRun({
                  text: date,
                  font: "Calibri", size: 16, color: COLORS.textMute,
                })],
              })],
            }),
          ],
        })],
      }),
    ],
  });

  // V93.0 : footer 3 colonnes (signature à gauche · pagination centre · "Confidentiel" droite)
  // Compliance : ligne disclaimer juridique ajoutée au footer.
  // Apparaît sur chaque page du contenu — pas dispositif médical.
  const complianceDisclaimerPara = new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { before: 80, after: 0 },
    children: [new TextRun({
      text: PRACTITIONER_LEGAL_PROFILE.disclaimerShort,
      font: "Calibri", size: 13, italics: true, color: COLORS.textMute,
    })],
  });

  const docFooter = new Footer({
    children: [
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 6, color: "EEEAD9" },
          bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        },
        rows: [new TableRow({
          children: [
            new TableCell({
              width: { size: 33, type: WidthType.PERCENTAGE },
              borders: { top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }, bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }, left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }, right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" } },
              margins: { top: 100, bottom: 0, left: 0, right: 0 },
              children: [new Paragraph({
                alignment: AlignmentType.LEFT,
                children: [new TextRun({
                  text: "Anissa Deroubaix Nutrition",
                  font: "Calibri", size: 16, color: COLORS.textMute,
                })],
              })],
            }),
            new TableCell({
              width: { size: 34, type: WidthType.PERCENTAGE },
              borders: { top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }, bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }, left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }, right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" } },
              margins: { top: 100, bottom: 0, left: 0, right: 0 },
              children: [new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({ children: [PageNumber.CURRENT], font: "Calibri", size: 16, color: COLORS.textMute }),
                  new TextRun({ text: " / ", font: "Calibri", size: 16, color: COLORS.textMute }),
                  new TextRun({ children: [PageNumber.TOTAL_PAGES], font: "Calibri", size: 16, color: COLORS.textMute }),
                ],
              })],
            }),
            new TableCell({
              width: { size: 33, type: WidthType.PERCENTAGE },
              borders: { top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }, bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }, left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }, right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" } },
              margins: { top: 100, bottom: 0, left: 0, right: 0 },
              children: [new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [new TextRun({
                  text: "Confidentiel",
                  font: "Calibri", size: 16, color: COLORS.textMute,
                })],
              })],
            }),
          ],
        })],
      }),
      complianceDisclaimerPara,
    ],
  });

  // ─── V93.0 : CLOSING PAGE (signature + message) ───────────────────
  const closingChildren = [
    pSpacer(3000),
    // Petit séparateur doré centré
    new Paragraph({
      alignment: AlignmentType.CENTER,
      indent: { left: convertInchesToTwip(2.5), right: convertInchesToTwip(2.5) },
      border: { bottom: { color: COLORS.gold, size: 6, space: 6, style: "single" } },
      children: [new TextRun({ text: "" })],
    }),
    pSpacer(300),
    pCover("Ce plan a été élaboré spécifiquement pour vous", { size: 22, italic: true, color: COLORS.text }),
    pCover(`par ${PRACTITIONER_LEGAL_PROFILE.name}`, { size: 22, italic: true, color: COLORS.text }),
    pCover(PRACTITIONER_LEGAL_PROFILE.title + '.', { size: 18, italic: true, color: COLORS.textSoft }),
    pSpacer(280),
    pCover("Il est recommandé de suivre ce plan pendant 4 semaines", { size: 18, color: COLORS.textMute }),
    pCover("avant d'envisager des ajustements.", { size: 18, color: COLORS.textMute }),
    pSpacer(500),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      indent: { left: convertInchesToTwip(2.5), right: convertInchesToTwip(2.5) },
      border: { bottom: { color: COLORS.gold, size: 6, space: 6, style: "single" } },
      children: [new TextRun({ text: "" })],
    }),
    pSpacer(280),
    pCover(PRACTITIONER_LEGAL_PROFILE.name, { size: 22, bold: true, color: COLORS.green }),
    // Compliance : ligne signature avec titre exact (depuis practitionerProfile)
    pCover(getPractitionerSignatureLine(), { size: 14, color: COLORS.textMute }),
    pSpacer(120),
    pCover(`${PRACTITIONER_LEGAL_PROFILE.company} · Rue de Rive 28, 1260 Nyon`, { size: 14, color: COLORS.textMute }),
    pSpacer(120),
    // Mention validation + date (compliance : supervision humaine visible)
    pCover(`Plan validé le ${date}`, { size: 13, italic: true, color: COLORS.textMute }),
    pSpacer(280),
    // Disclaimer juridique complet (compliance : ne se substitue pas à un avis médical)
    new Paragraph({
      alignment: AlignmentType.CENTER,
      indent: { left: convertInchesToTwip(1.2), right: convertInchesToTwip(1.2) },
      children: [new TextRun({
        text: PRACTITIONER_LEGAL_PROFILE.disclaimer,
        font: "Calibri", size: 13, italics: true, color: COLORS.textMute,
      })],
    }),
    pSpacer(160),
    pCover("Document confidentiel — usage personnel uniquement", { size: 12, color: COLORS.textMute }),
  ];

  // ─── DOCUMENT ASSEMBLY ────────────────────────────────────────
  // Section 1 : cover (sans header/footer)
  // Section 2 : contenu (avec header + footer)
  const doc = new Document({
    creator: "Anissa Deroubaix Nutrition",
    title: `Plan nutrition - ${prenom}`,
    description: `Plan nutritionnel personnalisé pour ${prenom}`,
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
            },
            size: { orientation: PageOrientation.PORTRAIT },
          },
          titlePage: false,
        },
        children: coverChildren,
      },
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
            },
            size: { orientation: PageOrientation.PORTRAIT },
          },
        },
        headers: { default: docHeader },
        footers: { default: docFooter },
        children: contentChildren,
      },
      // V93.0 : section 3 = closing page (sans header/footer pour effet "fin")
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
            },
            size: { orientation: PageOrientation.PORTRAIT },
          },
          titlePage: false,
        },
        children: closingChildren,
      },
    ],
  });

  // Génère le blob et déclenche le download
  const blob = await Packer.toBlob(doc);
  // Sanitize date pour filename (Windows interdit /, \, :, *, ?, ", <, >, |)
  const safeDate = date.replace(/[\/\\:*?"<>|.]/g, '-');
  const filename = `plan-nutrition-${slugify(prenom)}-${safeDate}.docx`;
  // La preuve de remise porte sur les octets exacts du fichier téléchargé,
  // y compris les éléments éditoriaux (lettre, recettes, overrides), et non
  // sur le seul texte du plan. En cas d'indisponibilité du digest, aucun
  // téléchargement n'est déclenché : comportement fail-closed.
  const artifactSha256 = await sha256WordBlob(blob);
  // La construction du document peut durer. Le caller peut donc lier le
  // téléchargement à la cliente et à la version capturées au clic.
  if (typeof options.shouldCommitArtifact === 'function'
      && await options.shouldCommitArtifact() !== true) {
    throw new Error('La cliente ou la version a changé pendant la préparation du document.');
  }
  // Le document peut être long à construire. Relire la preuve après le blob,
  // au dernier instant avant tout saveAs, afin qu'une modification des repas,
  // recettes, compléments, overrides ou du profil invalide aussi le Word.
  await readCurrentPlanDeliveryEvidence({ client, consultation, db: supabase });
  let outputAuthorization = null;
  if (hasActiveMgd) {
    outputAuthorization = await mgdReviewV2Client.authorizeOutput({ clientId, consultationId,
      channel: 'word_export', planHash: persistedPlanHash, artifactSha256 });
  }
  if (usesExternalMode) {
    const externalAuthority = await externalPlanAuthorityClient.status({
      clientId, consultationId, planHash: persistedPlanHash,
      profileSummaryHash: externalInput.profile_summary_sha256,
    });
    if (!externalAuthority.authorized || !externalAuthority.persisted
        || !externalAuthority.validated) {
      throw new Error('Le plan externe a changé pendant la préparation du document. Export bloqué.');
    }
    saveAs(blob, filename);
    return { ok: true, filename, artifactSha256 };
  }
  // Toujours revalider au dernier instant, y compris si aucun rapport n'était
  // actif au début. Le serveur détecte ainsi un rapport apparu pendant la
  // construction du blob et refuse l'egress avant saveAs.
  const consumed = await mgdReviewV2Client.consumeOutput({ clientId, consultationId,
    authorizationId: outputAuthorization?.authorizationId || null,
    channel: 'word_export', artifactSha256 });
  if ((hasActiveMgd && (!consumed.required || consumed.planHash !== persistedPlanHash))
      || (!hasActiveMgd && consumed.required)) {
    throw new Error('Le dossier a changé pendant la préparation du document. Export bloqué.');
  }
  saveAs(blob, filename);

  return { ok: true, filename, artifactSha256 };
}

// ─── Helpers ────────────────────────────────────────────────────

function formatFrDate(dateInput) {
  try {
    const d = dateInput ? new Date(dateInput) : new Date();
    if (Number.isNaN(d.getTime())) return new Date().toLocaleDateString("fr-FR");
    return d.toLocaleDateString("fr-FR");
  } catch {
    return new Date().toLocaleDateString("fr-FR");
  }
}

function slugify(s) {
  return (s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
